import React, { useState, useEffect, useRef, useCallback } from "react";
import * as ProjektstandService from "../services/azureProjektstandService";

const STATUS_OPTIONS = [
  { value: "offen", label: "Offen", cssVar: "--text-bg-color" },
  { value: "in-bearbeitung", label: "In Bearbeitung", cssVar: "--second-color" },
  { value: "abgeschlossen", label: "Abgeschlossen", cssVar: "--main-color" },
];

const FORM_SECTIONS = [
  { id: "feedback-good", label: "Was läuft gut?" },
  { id: "feedback-bad", label: "Was läuft nicht gut?" },
  { id: "risk", label: "Wo sehen wir Risiken?" },
  { id: "improvement", label: "Wo sehen wir Potenziale?" },
  { id: "next-steps", label: "Was sind nächste Schritte?" },
];

const MATRIX_QUADRANTS = new Set([
  "urgent-important",
  "not-urgent-important",
  "urgent-not-important",
  "not-urgent-not-important",
]);

const quadrantFromPos = (posX, posY) => {
  const urgent = posX < 50;
  const important = posY < 50;
  if (urgent && important) return "urgent-important";
  if (!urgent && important) return "not-urgent-important";
  if (urgent && !important) return "urgent-not-important";
  return "not-urgent-not-important";
};

const StatusIcon = ({ status, onClick }) => {
  const opt = STATUS_OPTIONS.find((s) => s.value === status) || STATUS_OPTIONS[0];
  return (
    <button
      className="projektstand-status-icon"
      onClick={onClick}
      title={opt.label}
      style={{ color: `var(${opt.cssVar})` }}
    >
      {status === "offen" && (
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
          <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      )}
      {status === "in-bearbeitung" && (
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
          <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.5" />
          <circle cx="8" cy="8" r="3" fill="currentColor" />
        </svg>
      )}
      {status === "abgeschlossen" && (
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
          <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.15" />
          <path d="M5 8l2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </button>
  );
};

// ─── Matrix topic card (free-positioned) ───
const TopicCard = ({ topic, onEdit, onStatusChange, onDelete, isCompleting, onDragStart, onDragEnd }) => {
  const [isEditing, setIsEditing] = useState(!topic.title && !topic.text);
  const [editTitle, setEditTitle] = useState(topic.title);
  const [editText, setEditText] = useState(topic.text);
  const titleRef = useRef(null);

  useEffect(() => {
    if (isEditing && titleRef.current) titleRef.current.focus();
  }, [isEditing]);

  const handleSave = () => {
    if (!editTitle.trim() && !editText.trim()) { onDelete(topic.id); return; }
    onEdit(topic.id, { title: editTitle.trim(), text: editText.trim() });
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSave(); }
    if (e.key === "Escape") { setEditTitle(topic.title); setEditText(topic.text); setIsEditing(false); }
  };

  const cycleStatus = () => {
    const idx = STATUS_OPTIONS.findIndex((s) => s.value === topic.status);
    onStatusChange(topic.id, STATUS_OPTIONS[(idx + 1) % STATUS_OPTIONS.length].value);
  };

  return (
    <div
      className={`projektstand-topic-card ${isCompleting ? "completing" : ""}`}
      style={{ left: `${topic.posX}%`, top: `${topic.posY}%` }}
      draggable={!isEditing}
      onDragStart={(e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        e.dataTransfer.setData("text/plain", topic.id);
        e.dataTransfer.setData("offsetX", String(e.clientX - rect.left));
        e.dataTransfer.setData("offsetY", String(e.clientY - rect.top));
        e.dataTransfer.effectAllowed = "move";
        e.currentTarget.style.opacity = "0.4";
        onDragStart(topic.id);
      }}
      onDragEnd={(e) => { e.currentTarget.style.opacity = "1"; onDragEnd(); }}
    >
      <div className="topic-card-header">
        <StatusIcon status={topic.status} onClick={cycleStatus} />
        {isEditing ? (
          <input ref={titleRef} className="topic-title-input" value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)} onKeyDown={handleKeyDown} placeholder="Titel..." />
        ) : (
          <span className="topic-title" onDoubleClick={() => setIsEditing(true)}>{topic.title || "Kein Titel"}</span>
        )}
        <button className="topic-delete-btn" onClick={() => onDelete(topic.id)} title="Löschen">
          <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5">
            <line x1="2" y1="2" x2="10" y2="10" /><line x1="10" y1="2" x2="2" y2="10" />
          </svg>
        </button>
      </div>
      {isEditing ? (
        <div className="topic-card-body editing">
          <textarea className="topic-text-input" value={editText} onChange={(e) => setEditText(e.target.value)}
            onKeyDown={handleKeyDown} placeholder="Beschreibung..." rows={2} />
          <div className="topic-edit-actions">
            <button className="btn btn-primary btn-xs" onClick={handleSave}>OK</button>
            <button className="btn btn-secondary btn-xs" onClick={() => {
              setEditTitle(topic.title); setEditText(topic.text);
              if (!topic.title && !topic.text) onDelete(topic.id); else setIsEditing(false);
            }}>Abbrechen</button>
          </div>
        </div>
      ) : (
        topic.text && <div className="topic-card-body" onDoubleClick={() => setIsEditing(true)}><p className="topic-text">{topic.text}</p></div>
      )}
    </div>
  );
};

// ─── Risk row with responses ───
const RiskRow = ({ entry, onUpdate, onDelete, debouncedSave, projectCode, responsePlaceholder = "+ Reaktion", entryPlaceholder = "Eintrag...", showDeadline = false }) => {
  const [title, setTitle] = useState(entry.title);
  const [deadline, setDeadline] = useState(entry.text || "");
  const [responses, setResponses] = useState(entry.responses || []);
  const [newResponse, setNewResponse] = useState("");

  const save = (changes) => {
    const updated = { ...entry, ...changes, updatedAt: new Date().toISOString() };
    onUpdate(entry.id, changes);
    if (!entry.id.startsWith("temp_")) debouncedSave(projectCode, [updated]);
  };

  const addResponse = () => {
    if (!newResponse.trim()) return;
    const next = [...responses, newResponse.trim()];
    setResponses(next);
    setNewResponse("");
    save({ responses: next });
  };

  const removeResponse = (idx) => {
    const next = responses.filter((_, i) => i !== idx);
    setResponses(next);
    save({ responses: next });
  };

  return (
    <div className="ps-risk-row">
      <div className="ps-risk-left">
        <input
          className="ps-form-input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={() => { if (title !== entry.title) save({ title }); }}
          onKeyDown={(e) => { if (e.key === "Enter") { e.target.blur(); } }}
          placeholder={entryPlaceholder}
        />
        <button className="topic-delete-btn ps-row-delete" onClick={() => onDelete(entry.id)} title="Löschen">
          <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5">
            <line x1="2" y1="2" x2="10" y2="10" /><line x1="10" y1="2" x2="2" y2="10" />
          </svg>
        </button>
      </div>
      <div className="ps-risk-right">
        {responses.map((r, i) => (
          <div key={i} className="ps-response-tag">
            <span>{r}</span>
            <button className="ps-response-remove" onClick={() => removeResponse(i)}>&times;</button>
          </div>
        ))}
        <input
          className="ps-response-input"
          value={newResponse}
          onChange={(e) => setNewResponse(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addResponse(); } }}
          placeholder={responsePlaceholder}
        />
      </div>
      {showDeadline && (
        <div className="ps-risk-deadline">
          <input
            className="ps-form-input"
            value={deadline}
            onChange={(e) => setDeadline(e.target.value)}
            onBlur={() => { if (deadline !== entry.text) save({ text: deadline }); }}
            onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
            placeholder="Wann?"
          />
        </div>
      )}
    </div>
  );
};

// ─── Simple form row (gut / nicht gut / verbesserungen) ───
const SimpleRow = ({ entry, onUpdate, onDelete, debouncedSave, projectCode }) => {
  const [title, setTitle] = useState(entry.title);

  const save = (changes) => {
    const updated = { ...entry, ...changes, updatedAt: new Date().toISOString() };
    onUpdate(entry.id, changes);
    if (!entry.id.startsWith("temp_")) debouncedSave(projectCode, [updated]);
  };

  return (
    <div className="ps-simple-row">
      <input
        className="ps-form-input"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onBlur={() => { if (title !== entry.title) save({ title }); }}
        onKeyDown={(e) => { if (e.key === "Enter") { e.target.blur(); } }}
        placeholder="Eintrag..."
      />
      <button className="topic-delete-btn ps-row-delete" onClick={() => onDelete(entry.id)} title="Löschen">
        <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5">
          <line x1="2" y1="2" x2="10" y2="10" /><line x1="10" y1="2" x2="2" y2="10" />
        </svg>
      </button>
    </div>
  );
};

// ═══════════════════════════════════════════════
// Main modal
// ═══════════════════════════════════════════════
const ProjektstandModal = ({ projects, initialProject, user, onClose }) => {
  const [selectedProjectCode, setSelectedProjectCode] = useState(initialProject?.code || "");
  const [topics, setTopics] = useState([]);
  const [completedTopics, setCompletedTopics] = useState([]);
  const [formEntries, setFormEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [completingIds, setCompletingIds] = useState(new Set());
  const [draggedTopicId, setDraggedTopicId] = useState(null);
  const saveTimeoutRef = useRef(null);
  const completingTimers = useRef({});
  const canvasRef = useRef(null);

  useEffect(() => {
    if (selectedProjectCode) loadAllData(selectedProjectCode);
    else { setTopics([]); setCompletedTopics([]); setFormEntries([]); }
  }, [selectedProjectCode]);

  const loadAllData = async (projectCode) => {
    setLoading(true);
    try {
      const all = await ProjektstandService.getTopics(projectCode);
      setTopics(all.filter((t) => MATRIX_QUADRANTS.has(t.quadrant) && t.status !== "abgeschlossen"));
      setCompletedTopics(all.filter((t) => MATRIX_QUADRANTS.has(t.quadrant) && t.status === "abgeschlossen"));
      setFormEntries(all.filter((t) => !MATRIX_QUADRANTS.has(t.quadrant)));
    } catch (error) {
      console.error("Fehler beim Laden:", error);
    } finally {
      setLoading(false);
    }
  };

  const debouncedSave = useCallback((projectCode, updatedTopics) => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(async () => {
      try { await ProjektstandService.updateTopicsBatch(projectCode, updatedTopics); }
      catch (error) { console.error("Auto-save error:", error); }
    }, 800);
  }, []);

  useEffect(() => () => { if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current); }, []);

  // ─── Matrix handlers ───
  const handleAddTopicAt = (e) => {
    const el = canvasRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    let posX = ((e.clientX - rect.left) / rect.width) * 100;
    let posY = ((e.clientY - rect.top) / rect.height) * 100;
    posX = Math.max(1, Math.min(posX, 85));
    posY = Math.max(1, Math.min(posY, 90));
    setTopics((prev) => [...prev, {
      id: `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      projectCode: selectedProjectCode, title: "", text: "",
      quadrant: quadrantFromPos(posX, posY), status: "offen", sortOrder: 0,
      posX, posY, responses: [], completedAt: "",
      createdBy: user?.username || user?.name || "",
      createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(),
    }]);
  };

  const handleEditTopic = async (topicId, changes) => {
    setTopics((prev) => prev.map((t) => t.id === topicId ? { ...t, ...changes, updatedAt: new Date().toISOString() } : t));
    const topic = topics.find((t) => t.id === topicId);
    if (!topic) return;
    const updated = { ...topic, ...changes, updatedAt: new Date().toISOString() };
    if (topicId.startsWith("temp_")) {
      try {
        const created = await ProjektstandService.createTopic(selectedProjectCode, updated);
        setTopics((prev) => prev.map((t) => t.id === topicId ? { ...t, id: created.id } : t));
      } catch (error) { console.error("Create error:", error); }
    } else {
      debouncedSave(selectedProjectCode, [updated]);
    }
  };

  const undoCompletion = async (topicId) => {
    if (completingTimers.current[topicId]) { clearTimeout(completingTimers.current[topicId]); delete completingTimers.current[topicId]; }
    setCompletingIds((prev) => { const n = new Set(prev); n.delete(topicId); return n; });
    setTopics((prev) => prev.map((t) => t.id === topicId ? { ...t, status: "in-bearbeitung", completedAt: "", updatedAt: new Date().toISOString() } : t));
    const topic = topics.find((t) => t.id === topicId);
    if (topic && !topicId.startsWith("temp_")) debouncedSave(selectedProjectCode, [{ ...topic, status: "in-bearbeitung", completedAt: "", updatedAt: new Date().toISOString() }]);
  };

  const handleStatusChange = async (topicId, newStatus) => {
    if (completingIds.has(topicId)) { undoCompletion(topicId); return; }
    if (newStatus === "abgeschlossen") {
      setCompletingIds((prev) => new Set([...prev, topicId]));
      const topic = topics.find((t) => t.id === topicId);
      if (!topic) return;
      const updated = { ...topic, status: "abgeschlossen", completedAt: new Date().toISOString(), updatedAt: new Date().toISOString() };
      if (!topicId.startsWith("temp_")) { try { await ProjektstandService.updateTopic(selectedProjectCode, topicId, updated); } catch {} }
      setTopics((prev) => prev.map((t) => t.id === topicId ? updated : t));
      completingTimers.current[topicId] = setTimeout(() => {
        delete completingTimers.current[topicId];
        setTopics((prev) => prev.filter((t) => t.id !== topicId));
        setCompletedTopics((prev) => [updated, ...prev]);
        setCompletingIds((prev) => { const n = new Set(prev); n.delete(topicId); return n; });
      }, 5000);
    } else {
      setTopics((prev) => prev.map((t) => t.id === topicId ? { ...t, status: newStatus, updatedAt: new Date().toISOString() } : t));
      const topic = topics.find((t) => t.id === topicId);
      if (topic && !topicId.startsWith("temp_")) debouncedSave(selectedProjectCode, [{ ...topic, status: newStatus, updatedAt: new Date().toISOString() }]);
    }
  };

  const handleDeleteTopic = async (topicId) => {
    setTopics((prev) => prev.filter((t) => t.id !== topicId));
    if (!topicId.startsWith("temp_")) { try { await ProjektstandService.deleteTopic(selectedProjectCode, topicId); } catch {} }
  };

  const handleDeleteCompleted = async (topicId) => {
    setCompletedTopics((prev) => prev.filter((t) => t.id !== topicId));
    if (!topicId.startsWith("temp_")) { try { await ProjektstandService.deleteTopic(selectedProjectCode, topicId); } catch {} }
  };

  const handleRestoreCompleted = async (topicId) => {
    const topic = completedTopics.find((t) => t.id === topicId);
    if (!topic) return;
    const restored = { ...topic, status: "offen", completedAt: "", updatedAt: new Date().toISOString() };
    setCompletedTopics((prev) => prev.filter((t) => t.id !== topicId));
    setTopics((prev) => [...prev, restored]);
    if (!topicId.startsWith("temp_")) { try { await ProjektstandService.updateTopic(selectedProjectCode, topicId, restored); } catch {} }
  };

  const handleDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };

  const handleDrop = async (e) => {
    e.preventDefault();
    const topicId = e.dataTransfer.getData("text/plain");
    if (!topicId) return;
    const topic = topics.find((t) => t.id === topicId);
    if (!topic) { setDraggedTopicId(null); return; }
    const el = canvasRef.current;
    if (!el) { setDraggedTopicId(null); return; }
    const rect = el.getBoundingClientRect();
    const offsetX = parseFloat(e.dataTransfer.getData("offsetX")) || 0;
    const offsetY = parseFloat(e.dataTransfer.getData("offsetY")) || 0;
    let posX = ((e.clientX - rect.left - offsetX) / rect.width) * 100;
    let posY = ((e.clientY - rect.top - offsetY) / rect.height) * 100;
    posX = Math.max(0, Math.min(posX, 85));
    posY = Math.max(0, Math.min(posY, 90));
    const updated = { ...topic, quadrant: quadrantFromPos(posX, posY), posX, posY, updatedAt: new Date().toISOString() };
    setTopics((prev) => prev.map((t) => t.id === topicId ? updated : t));
    if (!topicId.startsWith("temp_")) debouncedSave(selectedProjectCode, [updated]);
    setDraggedTopicId(null);
  };

  // ─── Form entry handlers ───
  const getEntriesForSection = (sectionId) => formEntries.filter((e) => e.quadrant === sectionId);

  const handleAddFormEntry = async (sectionId) => {
    const entry = {
      id: `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      projectCode: selectedProjectCode, title: "", text: "",
      quadrant: sectionId, status: "offen", sortOrder: 0,
      posX: 0, posY: 0, responses: [],
      completedAt: "", createdBy: user?.username || user?.name || "",
      createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(),
    };
    setFormEntries((prev) => [...prev, entry]);
  };

  const handleUpdateFormEntry = async (entryId, changes) => {
    setFormEntries((prev) => prev.map((e) => e.id === entryId ? { ...e, ...changes, updatedAt: new Date().toISOString() } : e));
    const entry = formEntries.find((e) => e.id === entryId);
    if (!entry) return;
    const updated = { ...entry, ...changes, updatedAt: new Date().toISOString() };
    if (entryId.startsWith("temp_")) {
      if (!updated.title.trim()) return; // don't persist empty
      try {
        const created = await ProjektstandService.createTopic(selectedProjectCode, updated);
        setFormEntries((prev) => prev.map((e) => e.id === entryId ? { ...e, id: created.id } : e));
      } catch {}
    }
    // non-temp saves are handled by the row components via debouncedSave
  };

  const handleDeleteFormEntry = async (entryId) => {
    setFormEntries((prev) => prev.filter((e) => e.id !== entryId));
    if (!entryId.startsWith("temp_")) { try { await ProjektstandService.deleteTopic(selectedProjectCode, entryId); } catch {} }
  };

  const sortedProjects = [...projects].filter((p) => p.type === "project" || !p.type).sort((a, b) => b.code.localeCompare(a.code));

  return (
    <>
      <div className="overlay" onClick={onClose} />
      <div className="projektstand-modal">
        {/* Header */}
        <div className="projektstand-header">
          <div className="projektstand-header-left">
            <select className="projektstand-project-select" value={selectedProjectCode}
              onChange={(e) => setSelectedProjectCode(e.target.value)}>
              <option value="" disabled>-- Projekt wählen --</option>
              {sortedProjects.map((p) => <option key={p.code} value={p.code}>{p.code} - {p.title}</option>)}
            </select>
          </div>
          <h2 className="projektstand-title">Projektstand</h2>
          <button className="projektstand-close-btn" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="4" y1="4" x2="16" y2="16" /><line x1="16" y1="4" x2="4" y2="16" />
            </svg>
          </button>
        </div>

        {loading ? (
          <div className="projektstand-loading">Laden...</div>
        ) : !selectedProjectCode ? (
          <div className="projektstand-empty">Bitte Projekt auswählen</div>
        ) : (
          <div className="projektstand-pages">
            {/* ── PAGE 1: Matrix ── */}
            <section className="projektstand-page">
              <div className="projektstand-matrix">
                <div className="matrix-y-axis">
                  <span className="axis-label axis-label-top">Wichtig</span>
                  <span className="axis-label axis-label-bottom">Nicht wichtig</span>
                </div>
                <div className="matrix-content">
                  <div className="matrix-x-axis">
                    <span className="axis-label">Dringend</span>
                    <span className="axis-label">Nicht dringend</span>
                  </div>
                  <div ref={canvasRef} className="matrix-canvas"
                    onDragOver={handleDragOver} onDrop={handleDrop}
                    onDoubleClick={(e) => { if (e.target === e.currentTarget) handleAddTopicAt(e); }}>
                    {topics.map((topic) => (
                      <TopicCard key={topic.id} topic={topic} onEdit={handleEditTopic}
                        onStatusChange={handleStatusChange} onDelete={handleDeleteTopic}
                        isCompleting={completingIds.has(topic.id)}
                        onDragStart={setDraggedTopicId} onDragEnd={() => setDraggedTopicId(null)} />
                    ))}
                  </div>
                </div>
              </div>

              {completedTopics.length > 0 && (
                <div className="projektstand-completed">
                  <h3 className="completed-header">Abgeschlossen ({completedTopics.length})</h3>
                  <div className="completed-list">
                    {completedTopics.map((topic) => (
                      <div key={topic.id} className="completed-item">
                        <StatusIcon status="abgeschlossen" onClick={() => handleRestoreCompleted(topic.id)} />
                        <span className="completed-title" onClick={() => handleRestoreCompleted(topic.id)} title="Klicken zum Wiederherstellen">{topic.title}</span>
                        {topic.text && <span className="completed-text">{topic.text}</span>}
                        <span className="completed-date">{topic.completedAt ? new Date(topic.completedAt).toLocaleDateString("de-CH") : ""}</span>
                        <button className="topic-delete-btn" onClick={() => handleDeleteCompleted(topic.id)} title="Endgültig löschen">
                          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <line x1="2" y1="2" x2="10" y2="10" /><line x1="10" y1="2" x2="2" y2="10" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </section>

            {/* ── PAGE 2: Form sections ── */}
            <section className="projektstand-page">
              <div className="projektstand-form">
                {FORM_SECTIONS.map((section) => (
                  <div key={section.id} className="ps-form-section">
                    <div className="ps-section-header">
                      <h3>{section.label}</h3>
                      <button className="btn btn-secondary btn-xs" onClick={() => handleAddFormEntry(section.id)}>+</button>
                    </div>

                    {(section.id === "risk" || section.id === "feedback-bad" || section.id === "next-steps") ? (
                      <div className="ps-risk-list">
                        {getEntriesForSection(section.id).map((entry) => (
                          <RiskRow key={entry.id} entry={entry} onUpdate={handleUpdateFormEntry}
                            onDelete={handleDeleteFormEntry} debouncedSave={debouncedSave} projectCode={selectedProjectCode}
                            entryPlaceholder={section.id === "next-steps" ? "Nächster Schritt..." : section.id === "risk" ? "Risiko beschreiben..." : "Eintrag..."}
                            responsePlaceholder={section.id === "next-steps" ? "+ Verantwortlich" : "+ Reaktion"}
                            showDeadline={section.id === "next-steps"} />
                        ))}
                      </div>
                    ) : (
                      <div className="ps-simple-list">
                        {getEntriesForSection(section.id).map((entry) => (
                          <SimpleRow key={entry.id} entry={entry} onUpdate={handleUpdateFormEntry}
                            onDelete={handleDeleteFormEntry} debouncedSave={debouncedSave} projectCode={selectedProjectCode} />
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          </div>
        )}
      </div>
    </>
  );
};

export default ProjektstandModal;
